import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { SkeletonLoader } from '@/components/common/StateViews';
import { fetchCoverage } from '@/services/anatel';
import { cn } from '@/lib/cn';

/**
 * What the adjudicative screens cover, against what ANATEL actually decides.
 *
 * Loaded on its own query rather than as part of the overview payload: counting
 * the 1.8M-process, 15.4M-document registry costs seconds, and the landing page
 * should not wait on it. The panel fills in when it is ready.
 *
 * The denominator is the ADJUDICATIVE universe, not all 1.84M processes.
 * Measuring against everything would report ~1% coverage of a corpus that is 95%
 * licensing and fee collection — true, and a misleading way to describe a
 * product that never intended to analyse those.
 */
export function CoveragePanel() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { data, isPending, error } = useQuery({
    queryKey: ['anatel', 'coverage'],
    queryFn: fetchCoverage,
    staleTime: 10 * 60 * 1000,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  // A failure here must not take the page down: the overview's own figures are
  // independent of it. Render nothing rather than an error block.
  if (error) return null;

  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('anatel:coverage.title')}
      </h2>

      {isPending ? (
        <SkeletonLoader rows={3} />
      ) : (
        <Answer
          answer={data.coverage}
          help={
            // Interpolated with the count the panel itself measured: the help
            // text used to hardcode "1,8 milhão", which the registry outgrows
            // silently while the live figure renders a few pixels away.
            data.coverage.value
              ? t('anatel:coverage.help', {
                  universe: number.format(data.coverage.value.registry_processes),
                })
              : undefined
          }
        >
          {(value) => (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Figure
                  label={t('anatel:coverage.analysed')}
                  value={percent.format(value.adjudicative_coverage)}
                  hint={t('anatel:coverage.ofAdjudicative', {
                    held: number.format(value.analysed_processes),
                    total: number.format(value.registry_adjudicated),
                  })}
                />
                <Figure
                  label={t('anatel:coverage.universe')}
                  value={number.format(value.registry_processes)}
                  hint={t('anatel:coverage.documents', {
                    n: number.format(value.registry_documents),
                  })}
                />
                <Figure
                  label={t('anatel:coverage.routine')}
                  value={percent.format(value.routine_share)}
                  hint={t('anatel:coverage.routineHint')}
                />
              </div>

              <ul className="space-y-1.5">
                {value.tracks.map((track) => (
                  <li
                    key={track.key}
                    className="flex flex-wrap items-baseline justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <span className="font-medium text-foreground">
                      {t(`anatel:coverage.tracks.${track.key}`, { defaultValue: track.key })}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {track.processes > 0
                        ? t('anatel:coverage.trackCounts', {
                            processes: number.format(track.processes),
                            decisions: number.format(track.decisions),
                          })
                        : t('anatel:coverage.trackDecisionsOnly', {
                            decisions: number.format(track.decisions),
                          })}
                    </span>
                  </li>
                ))}
              </ul>

              <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                {Object.entries(value.documents_by_tier).map(([tier, count]) => (
                  <div
                    key={tier}
                    className={cn(
                      'rounded border px-2 py-1',
                      tier === 'substantive_decision' ? 'border-primary/40 bg-primary/5' : '',
                    )}
                  >
                    <dt className="text-muted-foreground">
                      {t(`anatel:coverage.tiers.${tier}`, { defaultValue: tier })}
                    </dt>
                    <dd className="tabular-nums text-foreground">{number.format(count)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </Answer>
      )}
    </section>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
